import { View, ActivityIndicator, Alert } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"
import { useLocalSearchParams } from "expo-router"

import { ar } from "@src/i18n/ar"
import { trpc, formatDateForDisplay, formatPrice, getPaymentLabel, sharedStyles } from "@src/utils"
import { Text, Button, ScrollView, Header } from "@src/components"

export default function ReturnDetails() {
  const params = useLocalSearchParams<{ id: string }>()
  const utils = trpc.useUtils()

  const returnData = trpc.returns.get.useQuery({ id: Number(params.id) })
  const cancelReturn = trpc.returns.cancelReturn.useMutation()

  if (returnData.isLoading) {
    return (
      <>
        <Header title={ar.returnDetails} />
        <ActivityIndicator className="mt-2" />
      </>
    )
  }

  if (!returnData.data) return null

  const data = returnData.data
  const methodLabel = getPaymentLabel(data.refundMethod)
  const isActive = data.status === "active"

  function handleCancel() {
    Alert.alert(ar.cancelReturn, ar.confirmCancelReturn, [
      { text: ar.no, style: "cancel" },
      {
        text: ar.yes,
        style: "destructive",
        onPress: () => {
          cancelReturn.mutate(
            { returnId: data.id },
            {
              onSuccess: () => {
                utils.returns.list.invalidate()
                utils.returns.get.invalidate({ id: data.id })
              },
              onError: (err) => {
                Alert.alert(ar.error, err.message)
              },
            },
          )
        },
      },
    ])
  }

  return (
    <>
      <Header title={`${ar.returnNumber} #${data.id}`} />
      <ScrollView contentContainerStyle={sharedStyles.contentContainer} onRefresh={() => returnData.refetch()}>
        <View className="bg-white p-4 rounded-xl mb-4 shadow-sm border border-gray-100">
          <View className="flex-row justify-between mb-3 pb-3 border-b border-gray-200">
            <Text className="text-gray-600">{ar.returnNumber}</Text>
            <Text className="font-semibold">#{data.id}</Text>
          </View>

          <View className="flex-row justify-between mb-3">
            <Text className="text-gray-600">{ar.status}</Text>
            <View className="flex-row items-center gap-1.5">
              <View className={`w-2.5 h-2.5 rounded-full ${isActive ? "bg-green-500" : "bg-red-500"}`} />
              <Text className={`font-semibold ${isActive ? "text-green-600" : "text-red-600"}`}>{isActive ? ar.active : ar.cancelled}</Text>
            </View>
          </View>

          <View className="flex-row justify-between mb-3">
            <Text className="text-gray-600">{ar.date}</Text>
            <Text className="font-semibold">{formatDateForDisplay(data.createdAt, "D MMM YYYY • hh:mm a")}</Text>
          </View>

          <View className="flex-row justify-between mb-3">
            <Text className="text-gray-600">{ar.refundMethod}</Text>
            <Text className="font-semibold">{methodLabel}</Text>
          </View>

          {data.receiptId != null && (
            <View className="flex-row justify-between mb-3">
              <Text className="text-gray-600">{ar.linkedReceipt}</Text>
              <Text className="font-semibold text-blue-500">#{data.receiptId}</Text>
            </View>
          )}

          {data.orderId != null && (
            <View className="flex-row justify-between mb-3">
              <Text className="text-gray-600">{ar.linkedOrder}</Text>
              <Text className="font-semibold text-blue-500">#{data.orderId}</Text>
            </View>
          )}

          {data.reason && (
            <View className="flex-row justify-between pt-3 border-t border-gray-200">
              <Text className="text-gray-600">{ar.returnReason}</Text>
              <Text className="font-semibold flex-1 text-right">{data.reason}</Text>
            </View>
          )}

          {data.cancelledAt && (
            <View className="mt-3 pt-3 border-t border-gray-200">
              <View className="flex-row justify-between mb-2">
                <Text className="text-gray-500">{ar.cancelledAt}</Text>
                <Text className="text-gray-700">{formatDateForDisplay(data.cancelledAt, "D MMM YYYY • hh:mm a")}</Text>
              </View>
            </View>
          )}
        </View>

        {data.items && data.items.length > 0 && (
          <View className="bg-white p-4 rounded-xl mb-4 shadow-sm border border-gray-100">
            <Text className="font-bold text-gray-700 mb-3">{ar.returnItems}</Text>
            {data.items.map((item) => (
              <View key={item.id} className="py-2 border-b border-gray-100 last:border-b-0">
                <View className="flex-row justify-between mb-1">
                  <Text className="font-medium text-gray-900 flex-1">{item.name}</Text>
                  <Text className="font-semibold text-green-600">
                    {formatPrice(item.total)} {ar.egp}
                  </Text>
                </View>
                {item.weight ? (
                  <View>
                    <Text className="text-gray-500 text-sm">
                      {formatPrice(item.price)} {ar.egp} / {ar.kg}
                    </Text>
                    <Text className="text-gray-500 text-sm">
                      {formatPrice(Math.round((item.price * item.weight) / 1000))} {ar.egp} / {item.weight} {ar.gms}
                    </Text>
                    {item.quantity > 1 && (
                      <Text className="text-gray-400 text-sm">
                        {item.quantity} × {formatPrice(Math.round((item.price * item.weight) / 1000))} {ar.egp}
                      </Text>
                    )}
                  </View>
                ) : (
                  <Text className="text-gray-500 text-sm">
                    {item.quantity} × {formatPrice(item.price)}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        <View className="flex-row justify-between mb-4 py-3 border-t border-gray-300">
          <Text className="font-bold text-lg">{ar.refundTotal}</Text>
          <Text className="font-bold text-lg text-green-600">
            {formatPrice(data.totalRefund)} {ar.egp}
          </Text>
        </View>

        {isActive && (
          <Button loading={cancelReturn.isPending} onPress={handleCancel} className="bg-red-500">
            {ar.cancelReturn}
          </Button>
        )}

        <SafeAreaView edges={["bottom"]} />
      </ScrollView>
    </>
  )
}
